import { HttpEventType } from "@angular/common/http";
import { EventEmitter } from "@angular/core";
import { Component, Input, ViewChild, OnChanges, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef, OnInit, Output } from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { BaseError, CollectionChangedEventArgs, ComponentLocator, GenericServiceResponse, ModalService, NTSTranslatePipe, NumericPropertyViewModel, TextButtonComponent, ToastMessageService } from "@nts/std";
import { BehaviorSubject, filter, firstValueFrom, forkJoin, lastValueFrom, Observable, Subject, takeUntil, tap } from "rxjs";
import { UploadFileOutputDto } from "src/app/receipt-long-op/domain-models/dto/upload-file-output.dto";
import { SvgIconComponent } from '@ngneat/svg-icon';
import { NgFor, NgIf } from "@angular/common";
import { ProgressComponent } from "./progress/progress.component";
import { DndDirective } from "./dnd/dnd.directive";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { UploadFileSettingsViewModel } from "./modals/upload-file-settings/upload-file-settings.view-model";
import { UploadFileSettingsModalContainerComponent } from "./modals/upload-file-settings/upload-file-settings-modal-container.component";
import { Utils } from "./utils/utils";
import { LocalstorageHelper } from "@nts/std/utility";
import { BaseViewModelInterface, CoreOrchestratorViewModelInterface, InitializableViewModelInterface } from "@nts/std/interfaces";

export interface PropertyViewModelRowNumberInterface {
  rowNumber: NumericPropertyViewModel;
}

export interface FileUploadInterface {
  fileUploadOriginalFileName: string;
  fileUploadStorageName?: string;
  fileUploadFileSize?: number;
  fileObject?: File;
  filePreviewUrl: SafeResourceUrl;
  fileErrors: BaseError[];
}
export interface FileUploadStatusInterface extends FileUploadInterface {
  progress?: number;
}

export interface OldCollectionViewModel<TItemViewModel, TOrchestrator = CoreOrchestratorViewModelInterface> extends Array<TItemViewModel>, BaseViewModelInterface, InitializableViewModelInterface {
  collectionChanged: EventEmitter<CollectionChangedEventArgs<TItemViewModel>>;
}

export interface FileCollectionViewModelInterface extends OldCollectionViewModel<FileUploadStatusInterface & PropertyViewModelRowNumberInterface> {
  defaultColor: string;
  activeColor: string;
  hoverColor: string;
}

@UntilDestroy()
@Component({
  selector: 'nts-multiple-file-upload',
  templateUrl: './multiple-file-upload.component.html',
  styleUrls: ['./multiple-file-upload.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SvgIconComponent,
    NgIf,
    TextButtonComponent,
    ProgressComponent,
    NgFor,
    NTSTranslatePipe,
    DndDirective,
  ]
})
export class MultipleFileUploadComponent implements OnChanges, OnInit {

  /**
   * Utils class exposed to the template
   */
  Utils = Utils;

  @Input() editable = true;
  @Input() showOnlyFirst = false;
  @Input() disableMoveFile = false;
  @Input() hideFirst = false;
  @Input() fileFilterCallBack: (item: FileUploadStatusInterface) => boolean = () => true;
  @Input() files: FileCollectionViewModelInterface; // In ingresso passo i file già caricati
  @Input() isRemoteUploadEnabled = false;
  @Input() moveUpCallBack: (itemIndex: number) => Promise<void> = null;
  @Input() moveDownCallBack: (itemIndex: number) => Promise<void> = null;
  @Input() uploadCallBack: (item: FileUploadStatusInterface, file: File) => Observable<GenericServiceResponse<UploadFileOutputDto>> = null;
  @Input() viewFileCallBack: (item: FileUploadStatusInterface) => Observable<any> = null;
  @Input() addCallBack: () => Promise<FileUploadStatusInterface> = null;
  @Input() removeCallBack: (itemIndex: number) => Promise<void> = null;

  @Input() singleFileMode = false;


  @Output() filesChanged: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('fileDropRef') fileDropRef;

  isHover = false;
  isActive = false;
  overrideBorderColor = null;
  modalClosed$ = new Subject<void>();

  get visibleFiles(): FileUploadStatusInterface[] {
    if (!this.files) {
      return [];
    }

    this.files.sort((first, second) => {
      if (first.rowNumber.value < second.rowNumber.value) {
        return -1;
      }
      return first.rowNumber.value > second.rowNumber.value ? 1 : 0;
    });
    return this.files
      .filter((file) => this.fileFilterCallBack(file))
      .slice(this.hideFirst ? 1 : 0, this.showOnlyFirst ? 1 : undefined);
  }

  getFileIndex(file: FileUploadStatusInterface): number {
    return this.files.indexOf(file as FileUploadStatusInterface & PropertyViewModelRowNumberInterface);
  }

  constructor(
    private sanitizer: DomSanitizer,
    private readonly cd: ChangeDetectorRef,
    private readonly toastMessageService: ToastMessageService,
    private readonly modalService: ModalService,
    private readonly componentLocator: ComponentLocator
  ) {
    this.componentLocator.registerViewModelComponentAssociation(UploadFileSettingsViewModel, UploadFileSettingsModalContainerComponent);
  }

  ngOnInit(): void {
    this.handleOverridesColors();
  }

  mouseout(e) {
    this.isHover = false;
    this.handleOverridesColors();
  }

  onBlur(e) {
    this.isActive = false;
    this.handleOverridesColors();
  }

  onFocus(e) {
    this.isActive = true;
    this.handleOverridesColors();
  }

  mouseover(e) {
    this.isHover = true;
    this.handleOverridesColors();
  }

  handleOverridesColors() {
    if (!this.files.defaultColor || !this.files.activeColor || !this.files.hoverColor) {
      // devono essere impostate tutte e tre le variabili
      return;
    }

    this.overrideBorderColor = this.files.defaultColor;
    if (this.isActive) {
      this.overrideBorderColor = this.files.activeColor;
    }
    if (this.isHover) {
      this.overrideBorderColor = this.files.hoverColor;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Imposto il progress 100% nei file fià caricati
    if (changes['files']?.currentValue) {
      const arr: FileUploadStatusInterface[] = changes['files']?.currentValue;
      for (const singleFile of arr) {
        singleFile.progress = 100;

        // if (Utils.isFileImage(singleFile?.fileObject)) {
        //   const previewReader: FileReader = new FileReader();
        //   previewReader.onloadend = (e) => {
        //     singleFile.filePreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(e.target.result as string);
        //     singleFile.fileUploadOriginalFileName = singleFile.fileUploadOriginalFileName;
        //     this.cd.markForCheck();
        //   }
        //   previewReader.readAsDataURL(singleFile.fileObject);
        // }
      }
      this.files.collectionChanged.pipe(untilDestroyed(this)).subscribe(() => this.cd.detectChanges());
    }
  }

  onFileDropped(e: FileList) {
    if (this.editable) {
      this.uploadFiles(e);
    }
  }

  /**
   * Upload a file
   *
   * @param item
   * @param file
   * @returns
   */
  async uploadFile(item: FileUploadStatusInterface, file: File) {
    this.filesChanged.emit();
    return lastValueFrom(
      this.uploadCallBack(item, file).pipe(
        tap((httpEvent: any) => {
          if (httpEvent?.type === HttpEventType.UploadProgress) {
            item.progress = Math.round(100 * (httpEvent.loaded / httpEvent.total) * 0.75);
            this.cd.markForCheck();
          } else if (httpEvent?.result !== undefined && httpEvent?.errors !== undefined) {
            item.progress = 100;
            item.fileUploadStorageName = httpEvent.result?.storageFileName;
            item.fileErrors = httpEvent.errors;

            if (Utils.isFileImage(item?.fileObject)) {
              const previewReader: FileReader = new FileReader();
              previewReader.onloadend = (e) => {
                item.filePreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(e.target.result as string);
                this.cd.markForCheck();
              };
              previewReader.readAsDataURL(item.fileObject);
            }

            this.filesChanged.emit();
            this.cd.markForCheck();
          } else if (httpEvent?.type === HttpEventType.Sent) {
          } else if (httpEvent?.type === HttpEventType.Sent) {
            item.progress = 25;
            this.cd.markForCheck();
          }
        })
      )
    );
  }

  /**
   * Handle image upload
   *
   * @param file
   * @param isRemote
   * @returns
   */
  async handleImageUpload(file: File, isRemote: boolean) {
    const uploadFileSettingsViewModel = new UploadFileSettingsViewModel(file);

    await this.addPropertyToRecents({ obs: uploadFileSettingsViewModel.optimizeImageSize$, propertyName: 'optimizeImageSize', propertyPath: 'uploadFileSettingsModal' });

    const panelClass = ['upload-file-settings-modal'];

    if (window !== window.top || window.location.search.includes('iframe=true')) {
      panelClass.push('iframe');
    }

    const result = await this.modalService.showCustomModalWithResultAsync<string>(
      uploadFileSettingsViewModel,
      false,
      false,
      false,
      {
        disableClose: true,
        panelClass
      }
    );

    this.modalClosed$.next();

    if (result.cancel === false) {
      let optimizedBlob: Blob;
      try {
        const response = await fetch(result.result);
        if (!response.ok) {
          throw new Error(`Network response was not ok: ${response.statusText}`);
        }
        optimizedBlob = await response.blob();
      } catch (error) {
        console.error('Fetch error:', error);
        return;
      }
      const fileObject = new File([optimizedBlob], file.name, { type: optimizedBlob.type });
      const item = await this.addCallBack();
      item.fileObject = fileObject;
      item.fileUploadFileSize = item.fileObject.size;
      item.fileUploadOriginalFileName = item.fileObject.name;
      this.cd.markForCheck();

      if (isRemote) {
        await this.uploadFile(item, item.fileObject);
      } else {
        await this.simulateUpload(item, false);
      }
    }

    // reset the input value
    if (this.fileDropRef) {
      this.fileDropRef.nativeElement.value = '';
    }
  }

  /**
   * Upload files
   *
   * @param files
   */
  async uploadFiles(files: FileList) {
    const selectedFiles = Array.from(files);
    const filesToUpload = this.singleFileMode ? selectedFiles.slice(0, 1) : selectedFiles;

    if (this.isRemoteUploadEnabled) {
      if (filesToUpload.length === 1 && filesToUpload[0].type.startsWith('image')) {
        await this.handleImageUpload(filesToUpload[0], true);
      } else {
        const tasks$ = filesToUpload.map(async (file) => {
          const item = await this.addCallBack();
          item.fileObject = file;
          item.fileUploadFileSize = file.size;
          item.fileUploadOriginalFileName = item.fileObject.name;
          return this.uploadFile(item, file);
        });
        await firstValueFrom(forkJoin(tasks$));
      }
    } else {
      if (filesToUpload.length === 1 && filesToUpload[0].type.startsWith('image')) {
        await this.handleImageUpload(filesToUpload[0], false);
      } else {
        for (const singleFile of filesToUpload) {
          const item = await this.addCallBack();
          item.fileObject = singleFile;
          item.fileUploadFileSize = item.fileObject.size;
          item.fileUploadOriginalFileName = item.fileObject.name;
          this.cd.markForCheck();
          await this.simulateUpload(item, false);
        }
      }
    }

    this.cd.detectChanges();
  }

  /**
   * Simulate the upload of a file
   *
   * @param item
   * @param instant
   * @returns
   */
  simulateUpload(item: FileUploadStatusInterface, instant: boolean): Promise<void> {
    return new Promise((resolve, reject) => {

      item.progress = 0;
      setTimeout(() => {
        const progressInterval = setInterval(() => {
          if (item.progress === 100) {
            clearInterval(progressInterval);

            if (Utils.isFileImage(item?.fileObject)) {
              const previewReader: FileReader = new FileReader();
              previewReader.onloadend = (e) => {
                item.filePreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(e.target.result as string);
                this.cd.markForCheck();
              };
              previewReader.readAsDataURL(item.fileObject);
            }

            resolve();

          } else {
            item.progress = Math.min(item.progress + 5, 100);
          }
          this.cd.markForCheck();
        }, instant ? 0 : 20);
        this.cd.markForCheck();
      }, instant ? 0 : 500);
    });
  }

  /**
   * handle file from browsing
   */
  fileBrowseHandler(e) {
    this.uploadFiles(e.target.files);
  }

  viewFile(e) {
    this.viewFileCallBack(e);
  }

  async retryUpload(file: FileUploadStatusInterface) {
    file.fileErrors = [];
    await this.uploadFile(file, file.fileObject);
  }

  getKeyFromFile(item: FileUploadStatusInterface) {
    if (item?.fileUploadOriginalFileName?.toLowerCase().endsWith('avi')) {
      return 'avi-format';
    }
    if (item?.fileUploadOriginalFileName?.toLowerCase().endsWith('gif')) {
      return 'gif-format';
    }
    if (item?.fileUploadOriginalFileName?.toLowerCase().endsWith('jpeg') ||
      item?.fileUploadOriginalFileName?.toLowerCase().endsWith('jpg')) {
      return 'jpeg-format';
    }
    if (item?.fileUploadOriginalFileName?.toLowerCase().endsWith('svg')) {
      return 'svg-format';
    }
    if (item?.fileUploadOriginalFileName?.toLowerCase().endsWith('zip')) {
      return 'compress';
    }
    if (item?.fileUploadOriginalFileName?.toLowerCase().endsWith('mpeg')) {
      return 'mpeg-format';
    }
    if (item?.fileUploadOriginalFileName?.toLowerCase().endsWith('png')) {
      return 'png-format';
    }
    if (item?.fileUploadOriginalFileName?.toLowerCase().endsWith('raw')) {
      return 'raw-format';
    }
    if (item?.fileUploadOriginalFileName?.toLowerCase().endsWith('tif')) {
      return 'tif-format';
    }
    if (item?.fileUploadOriginalFileName?.toLowerCase().endsWith('tiff')) {
      return 'tiff-format';
    }
    if (item?.fileUploadOriginalFileName?.toLowerCase().endsWith('webp')) {
      return 'webp-format';
    }
    if (item?.fileUploadOriginalFileName?.toLowerCase().endsWith('mkv')) {
      return 'media-video';
    }
    return 'journal';
  }

  async moveUpFile(fileIndex: number) {
    await this.moveUpCallBack(fileIndex);
  }

  async moveDownFile(fileIndex: number) {
    await this.moveDownCallBack(fileIndex);
  }

  /**
   * Delete file from files list
   * @param index (File index)
   */
  async deleteFile(index: number) {
    await this.removeCallBack(index);
    this.fileDropRef.nativeElement.value = '';
    // this.onFilesChanged.emit(this.files);
    this.filesChanged.emit();
  }

  async addPropertiesToRecents(
    properties: ({ propertyName: string, propertyPath: string, obs: BehaviorSubject<string | boolean | number>; })[],
    userBarrier = true
  ) {
    for (const property of properties) {
      await this.addPropertyToRecents(property, userBarrier);
    }
  }

  /**
   * Reload the recents
   */
  async reloadRecents(userBarrier = true): Promise<{ [key: string]: string | boolean | number; }> {
    const recentsStorageKey = 'recents';
    const recentsRawValue = (await LocalstorageHelper.getStorageItem(recentsStorageKey, undefined, true, true, userBarrier));

    let recents: { [key: string]: string | boolean | number; } = {};

    if (recentsRawValue != null) {
      recents = recentsRawValue as { [key: string]: string | boolean | number; };
    }

    return recents;
  }

  /**
   * Write the recents
   */
  async writeRecents(recents: { [key: string]: string | boolean | number; }, userBarrier = true) {
    const recentsStorageKey = 'recents';
    await LocalstorageHelper.setStorageItem(recentsStorageKey, recents, undefined, true, true, userBarrier);
  }

  /**
   * Add a property to recents
   *
   * @param property
   * @param userBarrier
   */
  async addPropertyToRecents(
    property: { propertyName: string, propertyPath: string, obs: BehaviorSubject<string | boolean | number>; },
    userBarrier = true
  ) {
    const recents = await this.reloadRecents(userBarrier);

    if (property['obs'] != undefined) {
      const bs = property['obs'] as BehaviorSubject<string | boolean | number>;
      const propertyName = property['propertyName'] as string;
      const propertyPath = property['propertyPath'] as string;

      const bsStorageKey = [propertyPath, propertyName].filter((p) => p?.length > 0).join('.');
      const rawValue = recents[bsStorageKey];

      if (rawValue != undefined && rawValue != null) {
        bs.next(rawValue as string | boolean | number);
        // aspetto che il valore venga emesso
        await firstValueFrom(bs.pipe(takeUntil(this.modalClosed$), filter((x) => x == rawValue)));
      }

      bs.pipe(
        takeUntil(this.modalClosed$),
      ).subscribe(async () => {

        // rileggo il valore dallo storage perchè potrebbe essere cambiato
        const recents = await this.reloadRecents(userBarrier);
        recents[bsStorageKey] = bs.value;

        await this.writeRecents(recents, userBarrier);
      });
    } else {
      // property non è un BehaviorSubject
      // quindi non posso gestirlo
      throw new Error('addPropertyToRecents non gestisce il tipo della property passata!');
    }
  }

}
